/**
 * The Ask Led composer — the settled bottom-of-pane treatment from the
 * "Right pane edge options" design (option B, `askLed: "Composer"`).
 *
 * ## It replaces the bubble, not the Ask tab
 *
 * The bubble was a shortcut INTO the Ask tab; this is the same shortcut with
 * the design's settled shape: a fixed composer centred under the content
 * column, sitting on a fade so the page reads as scrolling under it. Send
 * spends the SAME single LLM call the bubble did (`POST /api/ask` — the
 * backend persists every exchange), then hands over to the Ask tab with
 * `?ask=`, which opens the persisted exchange from history (`AskLed`'s
 * handed-over contract). One question, one call, and the answer arrives on
 * the screen built to show its citations.
 *
 * ## Content scrolls UNDER it, on purpose
 *
 * The old bubble needed the whole pane inset (`ASK_BUBBLE_GUTTER`) because it
 * floated over one corner of a scrolling region and a control could be
 * scrolled underneath it invisibly. The composer spans the pane's bottom over
 * a gradient — scrolling under it is the design — and every scrolling body
 * ends in `ASK_COMPOSER_CLEARANCE` of padding instead, so any control can
 * always be scrolled fully above the composer. The wrapper is
 * `pointer-events: none`; only the card itself takes clicks.
 *
 * ## The suggestion strip arrives with the POINTER
 *
 * The pills say what this page can be asked about, and the rest of the time
 * they are two cards floating over the page's own last line. So their
 * presence follows how near the pointer is to the composer, but only AFTER
 * the pointer has paused there briefly. A cursor crossing the bottom of the
 * page must not summon pills it was merely passing. Once hover intent lands,
 * the strip still interpolates by distance like the macOS dock; leaving takes
 * it away, and it is never in the way of a reader who is not reaching for it.
 *
 * They stay MOUNTED throughout, so showing suggestions never moves the
 * composer. The page clearance follows the height of a growing draft.
 * `useSuggestionStrip` owns it.
 *
 * ## Scope and attach render as drawn
 *
 * The design ships four icon buttons. History opens the Ask tab (that is
 * where the history panel lives) and Send asks; Scope and Attach are drawn
 * inert, the same decision spec §6.3 records for the item list's filter
 * chips — they render exactly as designed until the feature behind them
 * exists.
 */

import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { askClientTimeZone, askTimeHorizon, fetchAskSuggestions, postMutation, type AskAnswer } from "../api";
import { C } from "../tokens";
import { ASK_COMPOSER_CLEARANCE, META, SIDEBAR_WIDTH } from "./chrome";
import { hasPageSuggestions, suggestionsFor, type AskPageContext } from "./askSuggestions";
import { AskMentionInput } from "./AskMentionInput";

// The composer's box is the design's own arithmetic (latest revision): an
// `.askbox` as wide as the content column (900), padded by the gutter minus
// a 22px deliberate overhang — `max(12px, 72 - 22)` — so the card reads as
// sitting OVER the column rather than inside it. The `.askbox` class carries
// the mobile padding override (see `main.tsx`).
const CONTENT_MAX_WIDTH = 900;

/**
 * How far from the composer the strip is fully out, and where it is fully in.
 *
 * Between the two the pills interpolate — the macOS dock's own trick: a
 * control that is CLOSE tells you it is about to be reachable, so the pointer
 * arrives on something already there rather than on something that pops into
 * existence under it.
 *
 * Measured from the composer card's box, upward and outward only: the card is
 * already at the bottom of the pane, so there is nothing below it to approach
 * from.
 */
const SUGGESTIONS_FAR = 120;
const SUGGESTIONS_NEAR = 35;
/** A pass through the composer's reach is usually navigation, not intent. */
const SUGGESTION_HOVER_INTENT_MS = 500;

/**
 * How near the pointer is to the composer, 0 (away) to 1 (on it).
 *
 * Distance to the card's box, not to its centre: a wide bar approached from
 * the left edge is exactly as near as one approached from the middle, and a
 * centre-based measure would make the ends feel dead.
 */
function proximityTo(box: DOMRect, x: number, y: number): number {
  const dx = Math.max(box.left - x, 0, x - box.right);
  // Only upward — below the card is off the bottom of the pane.
  const dy = Math.max(box.top - y, 0);
  const distance = Math.hypot(dx, dy);
  if (distance <= SUGGESTIONS_NEAR) return 1;
  if (distance >= SUGGESTIONS_FAR) return 0;
  return (SUGGESTIONS_FAR - distance) / (SUGGESTIONS_FAR - SUGGESTIONS_NEAR);
}

/**
 * The suggestion strip's presence, as a NUMBER rather than a flag.
 *
 * It used to appear for five seconds on arrival and then withdraw on a timer,
 * coming back when the pointer entered a box. Two problems with that: the
 * pills were in the way of the page for five seconds nobody asked for, and
 * crossing the box edge made them snap in — a jump under a moving pointer
 * (user-directed, 2026-09-07).
 *
 * Presence follows the pointer's distance only after hover intent has landed.
 * The short pause distinguishes arriving at the composer from passing over it;
 * afterward the pills still fade and rise in proportion to distance.
 *
 * Two ways in that a pointer cannot provide, both pinned to 1:
 *  - **focus**, which is the keyboard's approach, and
 *  - **a touch device**, which has no pointer at all. Left to the pointer
 *    alone the strip would never appear on a tablet.
 */
function useSuggestionStrip(
  active: boolean,
  cardRef: RefObject<HTMLElement | null>,
) {
  const coarse =
    typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
  const [near, setNear] = useState(coarse ? 1 : 0);
  const [focused, setFocused] = useState(false);
  const [hoverIntent, setHoverIntent] = useState(coarse);
  const frame = useRef(0);

  useEffect(() => {
    // Touch has no hover and keyboard focus is handled separately below.
    if (coarse) {
      setHoverIntent(true);
      return;
    }
    if (!active || near <= 0.05) {
      setHoverIntent(false);
      return;
    }

    // Every meaningful pointer move changes `near` and restarts this timer.
    // The only way it completes is by actually lingering close to the bar.
    const timer = window.setTimeout(
      () => setHoverIntent(true),
      SUGGESTION_HOVER_INTENT_MS,
    );
    return () => window.clearTimeout(timer);
  }, [active, coarse, near]);

  useEffect(() => {
    if (!active || coarse) return;

    const onMove = (event: PointerEvent) => {
      // One update per frame. `pointermove` fires far faster than paint, and
      // setting state on every event would re-render the composer dozens of
      // times a second to move the pills a pixel.
      if (frame.current) return;
      frame.current = requestAnimationFrame(() => {
        frame.current = 0;
        const box = cardRef.current?.getBoundingClientRect();
        if (!box) return;
        setNear(proximityTo(box, event.clientX, event.clientY));
      });
    };

    window.addEventListener("pointermove", onMove);
    return () => {
      window.removeEventListener("pointermove", onMove);
      if (frame.current) cancelAnimationFrame(frame.current);
      frame.current = 0;
    };
  }, [active, cardRef, coarse]);

  const presence = focused || hoverIntent ? (focused ? 1 : near) : 0;
  return {
    presence,
    /** Below this the strip takes no clicks and no tab stop — a pill nobody
     *  can see must not be sitting over the page's last line. */
    up: presence > 0.05,
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
  };
}

/** Just the workspace out of a console search string. The suggestion fetch
 *  keys on this rather than on the whole URL, so moving between pages does not
 *  re-request a list that does not vary by page. */
function workspaceOf(search: string): string {
  return new URLSearchParams(search).get("workspace") ?? "";
}

function IconButton({
  title,
  onClick,
  send,
  testId,
  disabled,
  children,
}: {
  title: string;
  onClick?: () => void;
  send?: boolean;
  testId?: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      className={`kb ${send ? "askc-send" : "askc-btn"}`}
      title={title}
      aria-label={title}
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 32,
        height: 32,
        borderRadius: 9,
        border: 0,
        background: send ? undefined : "transparent",
        color: C.sub,
        display: "grid",
        placeItems: "center",
        cursor: "pointer",
        padding: 0,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <svg
        width="17"
        height="17"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        {children}
      </svg>
    </button>
  );
}

export function AskComposer({
  search,
  onContinue,
  onOpenAsk,
  sidebarVisible,
  page,
}: {
  search: string;
  /** What the reader is looking at — see `askSuggestions.ts`. The shell has
   *  already decided this; re-deriving it from `search` here would let the
   *  strip's idea of the page drift from the one actually rendered. */
  page: AskPageContext;
  /** Hand the (already asked and persisted) question to the Ask tab. */
  onContinue: (question: string) => void;
  /** Open the Ask tab with nothing in hand — the History button. */
  onOpenAsk: () => void;
  /** Whether the sidebar currently occupies its column — the rail starts
   *  where the pane does, so a collapsed sidebar moves it to the edge. */
  sidebarVisible: boolean;
}) {
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /**
   * The server's context-aware list — a FALLBACK, not the source.
   *
   * Fetched once per workspace rather than per page, because it does not vary
   * by page (that is the whole reason `askSuggestions` exists). Its own route
   * says a failure must not block the composer, so a rejection leaves this
   * empty and the built-ins take over.
   */
  const [serverSuggestions, setServerSuggestions] = useState<string[]>([]);
  const pageAnswers = hasPageSuggestions(page);
  useEffect(() => {
    // Not fetched at all when the page has its own questions: the server's
    // list is the fallback, and a request whose answer is discarded is a
    // request that should not be made.
    if (pageAnswers) return;
    let live = true;
    void fetchAskSuggestions(search).then(
      (list) => live && setServerSuggestions(list),
      () => {},
    );
    return () => {
      live = false;
    };
    // Workspace, not page — see above. `search` carries `?workspace=`.
  }, [workspaceOf(search), pageAnswers]);

  const suggestions = suggestionsFor(page, serverSuggestions);
  /**
   * The strip is for a reader who has not started.
   *
   * It disappears the moment there is a question in the box or one in flight:
   * suggestions competing with what you are already typing is clutter, and a
   * pill you can click mid-question would throw the sentence away.
   */
  const showSuggestions = suggestions.length > 0 && question.trim() === "" && !asking;

  // The strip is MOUNTED for as long as it is relevant and merely withdraws
  // — see `useSuggestionStrip`. Keeping it in the layout is deliberate: the
  // suggestions do not change the composer height, and the
  // page does not lurch every time the pills come and go.
  const cardRef = useRef<HTMLDivElement | null>(null);
  const railRef = useRef<HTMLDivElement | null>(null);
  const strip = useSuggestionStrip(showSuggestions, cardRef);

  // The clearance every page reads (`--ask-composer-clearance`) is published
  // during mount, before the first paint, not on the observer's first round.
  // That round lands a frame later (the observer reports in the rendering
  // step, and the write waits for the next animation frame). Until then every
  // page laid out against the 208px fallback: the records table and its
  // skeleton ended 9px low at 768px and above and 52px low at 390px, then
  // jumped up. Later size changes (the draft growing, the strip withdrawing)
  // still go through the observer.
  useLayoutEffect(() => {
    const rail = railRef.current;
    const shell = rail?.parentElement;
    if (!rail || !shell || typeof ResizeObserver === "undefined") return;
    const publish = () => {
      const clearance = Math.max(ASK_COMPOSER_CLEARANCE, Math.ceil(rail.getBoundingClientRect().height) + 60);
      shell.style.setProperty("--ask-composer-clearance", `${clearance}px`);
    };
    publish();
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(publish);
    });
    observer.observe(rail);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      shell.style.removeProperty("--ask-composer-clearance");
    };
  }, []);

  const ask = async (text: string) => {
    const query = text.trim();
    if (!query || asking) return;
    setAsking(true);
    setProblem(null);

    // One call, then hand over. The backend persists the exchange the moment
    // it answers, so the Ask tab's `?ask=` lookup finds it in history and
    // opens the conversation rather than re-asking (and re-billing) it.
    const result = await postMutation<AskAnswer>("/api/ask", search, {
      query,
      timeZone: askClientTimeZone(),
      timeHorizon: askTimeHorizon(search),
    });
    setAsking(false);
    if (!result.ok) {
      setProblem(
        result.configured
          ? (result.error ?? `Ask Led AI could not answer that (${result.status}).`)
          : "Ask Led AI runs on the LedAI backend, and this console is not configured to reach it. Nothing was sent.",
      );
      return;
    }
    setQuestion("");
    onContinue(query);
  };

  return (
    <div
      data-testid="ask-composer-rail"
      ref={railRef}
      style={{
        position: "fixed",
        left: sidebarVisible ? SIDEBAR_WIDTH : 0,
        // 9px off the true edge (the design's own allowance for the pane's
        // stable scrollbar gutter), so the card never sits under the bar.
        right: 9,
        bottom: 0,
        padding: "0 0 16px",
        zIndex: 5,
        pointerEvents: "none",
        display: "flex",
        justifyContent: "center",
        background: `linear-gradient(180deg, ${C.surfaceFade} 0%, ${C.surface} 46%)`,
      }}
    >
      <div
        className="askbox"
        style={{
          width: `min(100%, ${CONTENT_MAX_WIDTH}px)`,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
      {/*
        The suggestions, floating over the page ABOVE the composer.

        They are what the reader could ask about THIS page — see
        `askSuggestions.ts` for where they come from and why the console
        answers that rather than the server. They rise in with a per-pill
        delay so the strip arrives as a short sequence rather than a block,
        and they are keyed on their own text so moving to a page with
        different questions replays the entrance instead of swapping the words
        underneath a pill that never moved.

        Then they withdraw — `data-up="false"`, a fade and a small settle in
        `main.tsx` — and come back when the pointer nears the composer. While
        withdrawn they take no clicks and no tab stop, so nothing invisible is
        sitting over the page's last line.
      */}
      {showSuggestions && (
        <div
          data-testid="ask-suggestions"
          className="asksug-strip"
          data-up={strip.up ? "true" : "false"}
          data-presence={strip.presence.toFixed(2)}
          aria-hidden={!strip.up}
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            pointerEvents: strip.up ? "auto" : "none",
            // Interpolated, not toggled — the pills rise and fade with the
            // pointer's distance. No CSS transition on these two: the pointer
            // IS the clock, and easing on top of it would lag the hand.
            opacity: strip.presence,
            transform: `translateY(${((1 - strip.presence) * 8).toFixed(1)}px)`,
          }}
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion}
              type="button"
              className="kb asksug"
              data-testid="ask-suggestion"
              tabIndex={strip.up ? 0 : -1}
              onClick={() => void ask(suggestion)}
              style={{
                // The stagger is an inline delay on a class-declared
                // animation, the same arrangement the Ask dots use: with
                // motion off the animation is never declared and the delay
                // rides on nothing, so the pills are simply there.
                animationDelay: `${index * 70}ms`,
                fontSize: 14.37,
                fontWeight: 600,
                color: C.ink,
                background: C.surface,
                border: `1px solid ${C.line2}`,
                borderRadius: 999,
                padding: "8px 14px",
                boxShadow: `0 4px 14px ${C.line}`,
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
      <div
        data-testid="ask-composer"
        ref={cardRef}
        style={{
          pointerEvents: "auto",
          width: "100%",
          background: C.surface,
          border: `1px solid ${C.line2}`,
          borderRadius: 18,
          boxShadow: `0 8px 28px ${C.line}`,
          padding: "14px 14px 10px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <AskMentionInput
          search={search}
          placement="above"
          onValue={setQuestion}
          className="kb askc-input"
          data-testid="ask-composer-input"
          aria-label="Ask Led AI a question"
          value={question}
          disabled={asking}
          placeholder="Ask Led AI"
          // A touch or keyboard reader never sends a `pointermove`, so
          // reaching the composer at all is their way of asking for the
          // strip back.
          onFocus={strip.onFocus}
          onBlur={strip.onBlur}
          // `onInput`, not `onChange` — React 19 + happy-dom never synthesise
          // `change` for a text input; see `AskBubble` and `CommandK`.
          onInput={(event) => setQuestion(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void ask(question);
            }
          }}
          style={{
            fontSize: 17.25,
            color: C.ink,
            padding: "2px 4px",
            border: 0,
            outline: "none",
            background: "transparent",
            width: "100%",
          }}
        />

        {asking && (
          <div data-testid="ask-composer-pending" style={{ ...META, fontSize: 10.93, color: C.sub2, padding: "0 4px" }}>
            Reading your walks
          </div>
        )}
        {problem && (
          <div
            data-testid="ask-composer-error"
            style={{
              padding: "9px 12px",
              borderRadius: 11,
              background: C.redSoft,
              fontSize: 14.37,
              lineHeight: 1.5,
            }}
          >
            {problem}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 6 }}>
            <IconButton title="History" testId="ask-composer-history" onClick={onOpenAsk}>
              <circle cx="12" cy="12" r="8.5" />
              <path d="M12 8.2V12l2.8 1.8" />
            </IconButton>
            {/* Drawn inert, as designed — see the file header. */}
            <IconButton title="Scope">
              <path d="M4 7h16M4 12h16M4 17h16" />
              <circle cx="9" cy="7" r="2" />
              <circle cx="15" cy="12" r="2" />
              <circle cx="8" cy="17" r="2" />
            </IconButton>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <IconButton title="Attach">
              <path d="M16.5 7.5 9 15a2.5 2.5 0 0 0 3.5 3.5l6.5-6.5a4.5 4.5 0 0 0-6.4-6.4L6 11.8" />
            </IconButton>
            <IconButton
              title="Send"
              send
              testId="ask-composer-send"
              disabled={asking}
              onClick={() => void ask(question)}
            >
              <path d="M20 6v5a3 3 0 0 1-3 3H5" />
              <path d="M8.5 10.5 5 14l3.5 3.5" />
            </IconButton>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
